import React, { useContext, useEffect, useState, useRef } from "react"
import { Layout, Typography, Tooltip, Button, Badge, Avatar, Dropdown, Menu, notification as antdNotification } from "antd"
import { TitleContext } from "../../contexts/TitleContext"
import {
  Settings,
  MessageSquare,
  Clipboard,
  Maximize,
  Minimize,
  Bell,
  Sun,
  Moon,
  User,
} from "react-feather"
import { useTranslation } from "react-i18next"
import useQueryParams from "../../hooks/useQueryParams"
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint"
import "./Header.less"
import LogoutButton from "../../components/button/LogoutButton"
import { ThemeActions, ThemeContext } from "../../contexts/ThemeContex"
import Modal from "../../components/modal"
import SettingsModal from "../../components/modal/settings/SettingsModal"
import AddAdminForm from "../../components/form/AddAdminForm"
import ChatModal from "../../components/modal/ChatModal"
import DailyPlanModal from "../../components/modal/DailyPlanModal"
import NotificationsModal from "../../components/modal/NotificationsModal"
import UserProfileModal from "../../components/modal/UserProfileModal"
import { GET_PROFILE_PHOTO } from "../../rest-api/queries/user"
import { useQuery } from "@tanstack/react-query"
import { GET_CHAT_CHANNELS } from "../../rest-api/queries/chat"
import { GET_NOTIFICATIONS, MARK_NOTIFICATION_READ } from "../../rest-api/queries/notifications"
import { GET_TASKS } from "../../rest-api/queries/tasks"
import { playNotificationSound } from "../../utils/notificationSounds"
import { AuthContext } from "../../contexts/auth/AuthContext"
import { UI_LANGS } from "../../utils/uiLanguages"

export default function HeaderHome(props: any) {
  const { title } = useContext(TitleContext)
  const { sm } = useBreakpoint()
  const { themeState, themeDispatch } = useContext(ThemeContext)
  const { authState } = useContext(AuthContext)
  const query = useQueryParams()
  const { t, i18n } = useTranslation()

  // Tanlangan til localStorage da saqlanadi va sahifa qayta ochilganda tiklanadi
  const currentUiLang = (i18n.language || "ru").split("-")[0]
  const changeLanguage = (lng: string) => {
    if (lng === currentUiLang) return
    localStorage.setItem("i18nextLng", lng)
    i18n.changeLanguage(lng)
    /*
      Jadval ustunlari 23 ta faylda modul yuklanganda bir marta tarjima
      qilinadi va keyin muzlab qoladi; antd kalendari ham ilova ishga
      tushganda tanlanadi. Shuning uchun tilni to'liq almashtirishning
      yagona ishonchli yo'li — sahifani qayta yuklash.
    */
    window.location.reload()
  }
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  const [planOpen, setPlanOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [profileModalOpen, setProfileModalOpen] = useState(false)
  const [profilePhoto, setProfilePhoto] = useState<string>(
    localStorage.getItem("user_profile_photo") || ""
  )

  // Chat yopiq turganda ham o'qilmagan xabarlar soni ko'rinib turadi
  const { data: chatChannels = [] } = useQuery(
    ["GET_CHAT_CHANNELS_BADGE"],
    GET_CHAT_CHANNELS,
    { refetchInterval: 20000, refetchOnWindowFocus: true }
  )
  const totalUnreadChat = chatChannels.reduce(
    (sum, c) => sum + (c.unreadCount || 0),
    0
  )

  const isSuperAdmin = authState.user?.userName === "Administrator"

  // Qarzdorlik va qoldiq eslatmalari soni qo'ng'iroqcha ustida turadi
  const { data: notifsData } = useQuery(
    ["GET_NOTIFICATIONS_BADGE"],
    () => GET_NOTIFICATIONS({ unreadOnly: true, page: 1, pageSize: 1 }),
    { refetchInterval: 60000, refetchOnWindowFocus: true, enabled: isSuperAdmin }
  )
  const unreadAlerts = notifsData?.unreadCount ?? notifsData?.UnreadCount ?? 0

  // Topshiriqlar tugmasidagi son: menejerda hali o'qilmagan topshiriqlar,
  // adminda esa hisoboti tekshirilmagan topshiriqlar soni
  const { data: taskCountData } = useQuery(
    ["GET_TASKS_BADGE", isSuperAdmin],
    () => GET_TASKS({ status: isSuperAdmin ? "all" : "new", pageNumber: 1, pageSize: 200 }),
    { refetchInterval: 30000, refetchOnWindowFocus: true }
  )
  const newTaskCount = (() => {
    const items: any[] = taskCountData?.data || taskCountData?.Data || []
    if (isSuperAdmin) {
      return items.filter((x) => {
        const st = x.status ?? x.Status
        const approved = x.isApproved ?? x.IsApproved
        return (st === "completed" || st === "failed" || st === "cancelled") && !approved
      }).length
    }
    return items.filter((x) => !(x.isRead ?? x.IsRead)).length
  })()

  // Menejerga biriktirilgan topshiriq haqida real vaqtda ekranda xabar.
  // Qo'ng'iroqcha menejerda yo'q, shuning uchun xabar to'g'ridan-to'g'ri
  // ekran burchagida chiqadi va shu zahoti o'qilgan deb belgilanadi.
  const shownTaskIds = useRef<Set<number>>(new Set())
  const { data: taskNotifs } = useQuery(
    ["GET_TASK_NOTIFICATIONS"],
    () => GET_NOTIFICATIONS({ unreadOnly: true, page: 1, pageSize: 20 }),
    { refetchInterval: 15000, refetchOnWindowFocus: true, enabled: !isSuperAdmin }
  )

  useEffect(() => {
    if (isSuperAdmin) return
    const items: any[] = taskNotifs?.data || taskNotifs?.Data || []
    items
      .filter((n) => (n.type ?? n.Type) === "task")
      .forEach((n) => {
        const id = n.id ?? n.Id
        if (shownTaskIds.current.has(id)) return
        shownTaskIds.current.add(id)
        antdNotification.info({
          message: n.title ?? n.Title,
          description: n.message ?? n.Message,
          placement: "topRight",
          duration: 12,
          onClick: () => setPlanOpen(true),
          style: { cursor: "pointer" },
        })
        playNotificationSound()
        MARK_NOTIFICATION_READ(id).catch(() => {})
      })
  }, [taskNotifs, isSuperAdmin])

  useEffect(() => {
    const handleUpdate = () => {
      setProfilePhoto(localStorage.getItem("user_profile_photo") || "")
    }
    window.addEventListener("profile_photo_updated", handleUpdate)

    GET_PROFILE_PHOTO().then((res: any) => {
      const url = res?.PhotoUrl || res?.photoUrl
      const fileName = res?.FileName || res?.fileName
      if (url) {
        setProfilePhoto(url)
        localStorage.setItem("user_profile_photo", url)
        if (fileName) localStorage.setItem("user_profile_photo_filename", fileName)
      }
    }).catch(() => {})

    return () => window.removeEventListener("profile_photo_updated", handleUpdate)
  }, [])

  const isDark = themeState.currentTheme === "dark"

  const toggleTheme = () => {
    themeDispatch({
      type: ThemeActions.ChangeTheme,
      payload: {
        currentTheme: isDark ? "default" : "dark",
      },
    })
  }

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {})
      setIsFullscreen(true)
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {})
        setIsFullscreen(false)
      }
    }
  }

  return (
    <>
      <Layout.Header className="site-layout-header crm-top-header">
        <div className="header-left-section">
          <Typography.Title
            className="TitkeHeader_r header-title-text"
            style={{ margin: 0 }}
            level={4}
          >
            {title || t("home")}
          </Typography.Title>
        </div>

        <div className="HeaderWrapper_r header-right-section">
          {sm && (
            <div className="header-quick-icons">
              {/* 1 - Glavniy admin va menejerlar chati */}
              <Tooltip placement="bottom" title={t("adminManagersChat")}>
                {/* O'qilmagan xabarlar soni Telegram uslubida qizil raqam bilan */}
                <Badge
                  count={totalUnreadChat}
                  size="small"
                  overflowCount={99}
                  offset={[-4, 4]}
                  style={{ backgroundColor: "#EF4444" }}
                >
                  <Button
                    type="text"
                    shape="circle"
                    icon={<MessageSquare size={18} />}
                    className="header-icon-btn"
                    onClick={() => setChatOpen(true)}
                  />
                </Badge>
              </Tooltip>

              {/* 2 - Glavniy admin kunlik ish rejani kiritib qo'yishi va istoriyasida qolishi */}
              <Tooltip placement="bottom" title={t("dailyWorkPlan")}>
                {/* Yangi (o'qilmagan) topshiriqlar soni qizil raqam bilan */}
                <Badge
                  count={newTaskCount}
                  size="small"
                  overflowCount={99}
                  offset={[-4, 4]}
                  style={{ backgroundColor: "#EF4444" }}
                >
                  <Button
                    type="text"
                    shape="circle"
                    icon={<Clipboard size={18} />}
                    className="header-icon-btn"
                    onClick={() => setPlanOpen(true)}
                  />
                </Badge>
              </Tooltip>

              {/*
                3 - Eslatmalar: qarzdorlik va tovar qoldiqlari.
                Qo'ng'iroqcha FAQAT Bosh adminda ko'rinadi — menejerlarga
                bu bo'lim kerak emas (guruh talabi).
              */}
              {isSuperAdmin && (
                <Tooltip placement="bottom" title={t("alertsTooltip")}>
                  <Badge
                    count={unreadAlerts}
                    size="small"
                    overflowCount={99}
                    offset={[-4, 4]}
                    style={{ backgroundColor: "#EF4444" }}
                  >
                    <Button
                      type="text"
                      shape="circle"
                      icon={<Bell size={18} />}
                      className="header-icon-btn"
                      onClick={() => setNotifOpen(true)}
                    />
                  </Badge>
                </Tooltip>
              )}

              {/* Til tanlash: qisqa kod va bayroqcha (uz / ru / en) */}
              <Dropdown
                trigger={["click"]}
                overlay={
                  <Menu
                    selectedKeys={[currentUiLang]}
                    onClick={({ key }) => changeLanguage(String(key))}
                  >
                    {UI_LANGS.map((l) => (
                      <Menu.Item key={l.code}>
                        <span style={{ marginRight: 8 }}>{l.flag}</span>
                        {l.code.toUpperCase()}
                      </Menu.Item>
                    ))}
                  </Menu>
                }
              >
                <Button
                  type="text"
                  shape="circle"
                  className="header-icon-btn"
                  style={{ fontWeight: 700, fontSize: 12 }}
                >
                  {currentUiLang.toUpperCase()}
                </Button>
              </Dropdown>

              {/* Fullscreen */}
              <Tooltip placement="bottom" title={isFullscreen ? t("fullscreenOff") : t("fullscreenOn")}>
                <Button
                  type="text"
                  shape="circle"
                  icon={isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
                  onClick={toggleFullscreen}
                  className="header-icon-btn"
                />
              </Tooltip>

              {/* Instant Day/Night Switcher button */}
              <Tooltip placement="bottom" title={isDark ? t("dayMode") : t("nightMode")}>
                <Button
                  type="text"
                  shape="circle"
                  icon={isDark ? <Sun size={18} color="#60A5FA" /> : <Moon size={18} />}
                  onClick={toggleTheme}
                  className="header-icon-btn"
                />
              </Tooltip>
            </div>
          )}

          <Tooltip placement="bottom" title="Foydalanuvchi Profili">
            <div
              onClick={() => setProfileModalOpen(true)}
              style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", marginRight: 8 }}
            >
              <Avatar
                size={36}
                src={profilePhoto || undefined}
                icon={!profilePhoto && <User size={18} />}
                style={{
                  backgroundColor: "#2563EB",
                  cursor: "pointer",
                  border: "2px solid rgba(255,255,255,0.2)",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                }}
              />
            </div>
          </Tooltip>

          <Tooltip placement="bottom" title="Tizim Sozlamalari">
            <Button
              size="large"
              className="buttonHeader_r headermargin_r"
              onClick={() => query.set("settings", "open")}
              icon={<Settings size={18} />}
            />
          </Tooltip>

          <LogoutButton />
        </div>
      </Layout.Header>

      {/* Quick Access Modals */}
      <UserProfileModal visible={profileModalOpen} onClose={() => setProfileModalOpen(false)} />
      <ChatModal visible={chatOpen} onClose={() => setChatOpen(false)} />
      <DailyPlanModal visible={planOpen} onClose={() => setPlanOpen(false)} />
      <NotificationsModal visible={notifOpen} onClose={() => setNotifOpen(false)} />

      {/* Global Settings & Admin Modals */}
      <Modal footer={null} matcher="settings">
        <SettingsModal />
      </Modal>
      <Modal title={t("addAdmin")} footer={null} matcher="add-admin">
        <AddAdminForm />
      </Modal>
    </>
  )
}
